import { useEffect, useMemo, useState } from "react";
import { Plus, Trash, MagnifyingGlass, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg, useApi, inr } from "../../lib/api";
import { GlassCard, Loader } from "../kit";
import { StudentFeeSummary } from "./StudentFeeSummary";

const statusOf = (r) => (Number(r.paid_amount) >= Number(r.amount) && r.amount ? "Paid" : Number(r.paid_amount) > 0 ? "Partial" : "Pending");

function StudentPicker({ onPick }) {
  const { data } = useApi("/students");
  const [q, setQ] = useState("");
  const hits = useMemo(() => (q.trim().length < 2 ? [] : (data || []).filter((s) => `${s.name} ${s.admission_no}`.toLowerCase().includes(q.toLowerCase())).slice(0, 8)), [q, data]);
  return (
    <div className="relative">
      <MagnifyingGlass size={16} className="absolute left-3.5 top-3.5 text-zinc-500" />
      <input className="field !pl-10" placeholder="Search student by name or admission no" value={q} onChange={(e) => setQ(e.target.value)} data-testid="fee-entry-student-search" />
      {hits.length > 0 && (
        <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0b0b14] shadow-2xl">
          {hits.map((s) => (
            <button key={s.id} className="flex w-full justify-between px-4 py-2.5 text-left text-sm hover:bg-white/5" onClick={() => { onPick(s.id); setQ(""); }} data-testid={`fee-entry-pick-${s.admission_no}`}>
              <span className="text-white">{s.name}</span><span className="font-mono text-xs text-zinc-500">{s.admission_no}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemRow({ r, k, types, set, remove }) {
  return (
    <div className="grid grid-cols-2 gap-2 rounded-2xl bg-white/[0.03] p-3 lg:grid-cols-[1.5fr_0.8fr_1fr_0.8fr_0.8fr_1.2fr_auto] lg:items-center" data-testid={`entry-row-${k}`}>
      <select className="field col-span-2 lg:col-span-1" value={r.fee_type_id} onChange={(e) => set(k, "fee_type_id", e.target.value)} data-testid={`entry-type-${k}`}>
        {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
      <input type="number" min={1} className="field" placeholder="Amount" value={r.amount} onChange={(e) => set(k, "amount", e.target.value)} data-testid={`entry-amount-${k}`} />
      <input type="date" className="field" value={r.due_date} onChange={(e) => set(k, "due_date", e.target.value)} data-testid={`entry-due-${k}`} />
      {r.whole_session ? <span className="text-xs text-zinc-400 lg:col-span-2">Whole session ({r.instalments} instalments)</span> : <>
        <input type="number" min={0} className="field" placeholder="Paid" value={r.paid_amount} onChange={(e) => set(k, "paid_amount", e.target.value)} data-testid={`entry-paid-${k}`} />
        <span className={`text-center text-xs font-semibold ${statusOf(r) === "Paid" ? "text-emerald-300" : statusOf(r) === "Partial" ? "text-amber-300" : "text-zinc-400"}`} data-testid={`entry-status-${k}`}>{statusOf(r)}</span></>}
      <input className="field col-span-2 lg:col-span-1" placeholder="Remarks" value={r.remarks} onChange={(e) => set(k, "remarks", e.target.value)} data-testid={`entry-remarks-${k}`} />
      <button className="btn-ghost !p-2 !text-rose-300 justify-self-end" onClick={() => remove(k)} data-testid={`entry-remove-${k}`}><Trash size={14} /></button>
    </div>
  );
}

export function FeeEntry() {
  const [sid, setSid] = useState("");
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [method, setMethod] = useState("cash");
  const [busy, setBusy] = useState(false);
  const { data: tdata } = useApi("/fees/types");
  const types = (tdata?.types || []).filter((t) => t.enabled);
  const load = async (id) => {
    const r = await api.get(`/fees/suggest/${id}`);
    setData(r.data);
    setRows([]);
  };
  useEffect(() => { if (sid) load(sid).catch((e) => toast.error(errMsg(e))); }, [sid]);
  const set = (k, f, v) => setRows(rows.map((x, i) => (i === k ? { ...x, [f]: v } : x)));
  const addSuggested = (it, whole) => setRows([...rows, { fee_type_id: it.fee_type_id, amount: it.amount, due_date: it.first_due, paid_amount: "", remarks: "", whole_session: whole, instalments: it.instalments }]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post("/fees/entry", { student_id: sid, academic_year: data.academic_year, items: rows.map((x) => ({ ...x, amount: Number(x.amount), paid_amount: Number(x.paid_amount) || 0, method })) });
      toast.success(`${r.data.created} fee item(s) saved${r.data.receipts.length ? ` · ${r.data.receipts.length} receipt(s)` : ""}`);
      await load(sid);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-6" data-testid="fee-entry">
      <GlassCard className="p-4 sm:p-6">
        <StudentPicker onPick={setSid} />
        {sid && !data && <Loader />}
        {data && (
          <div className="mt-6">
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1"><h3 className="font-display text-2xl text-white" data-testid="fee-entry-student-name">{data.student.name}</h3>
              <span className="text-sm text-zinc-400" data-testid="fee-entry-class">{data.student.admission_no} · {data.student.class_name} · Session {data.academic_year}</span></div>
            <div className="eyebrow mt-6 mb-3">Fees for {data.student.grade} — tap to add</div>
            {!data.items.length ? <p className="text-sm text-zinc-500" data-testid="fee-entry-no-plan">No fee structure set for {data.student.grade} this session. Add items manually or set it up in Fee settings.</p> : (
              <div className="flex flex-wrap gap-2">
                {data.items.map((it) => (
                  <span key={it.fee_type_id} className="inline-flex overflow-hidden rounded-full border border-white/10 text-xs">
                    <button className="px-3 py-1.5 hover:bg-blue-500/20" onClick={() => addSuggested(it, false)} data-testid={`suggest-${it.fee_type_id}`}>{it.fee_type} · {inr(it.amount)}{it.mandatory ? "" : " · optional"}</button>
                    {it.instalments > 1 && <button className="border-l border-white/10 px-3 py-1.5 text-blue-300 hover:bg-blue-500/20" onClick={() => addSuggested(it, true)} data-testid={`suggest-session-${it.fee_type_id}`}>×{it.instalments} session</button>}
                  </span>
                ))}
              </div>
            )}
            <div className="mt-5 space-y-2">{rows.map((r, k) => <ItemRow key={k} r={r} k={k} types={types} set={set} remove={(i) => setRows(rows.filter((_, j) => j !== i))} />)}</div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button className="btn-ghost" disabled={!types.length} onClick={() => setRows([...rows, { fee_type_id: types[0].id, amount: "", due_date: "", paid_amount: "", remarks: "", whole_session: false }])} data-testid="entry-add-row"><Plus size={15} /> Add fee item</button>
              <select className="field !w-44" value={method} onChange={(e) => setMethod(e.target.value)} data-testid="entry-method">{[["cash", "Cash"], ["upi", "UPI"], ["cheque", "Cheque"], ["bank_transfer", "Bank transfer"], ["card", "Card (POS)"]].map(([v, l]) => <option key={v} value={v}>Paid by {l}</option>)}</select>
              <span className="text-sm text-zinc-400 sm:ml-auto">Adding <span className="text-white" data-testid="entry-total">{inr(rows.reduce((a, r) => a + (Number(r.amount) || 0) * (r.whole_session ? r.instalments : 1), 0))}</span></span>
              <button className="btn-primary" disabled={busy || !rows.length || rows.some((r) => !(Number(r.amount) > 0))} onClick={save} data-testid="entry-save"><FloppyDisk size={15} /> Save fees</button>
            </div>
          </div>
        )}
      </GlassCard>
      {data && <StudentFeeSummary summary={data.summary} />}
    </div>
  );
}
